import React, { useContext, useEffect, useRef, useState } from "react"
import { Text, TextInput, TouchableOpacity, TouchableWithoutFeedback, View } from "react-native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { textStyles } from "../style"
import api from '../api-client'
import MFAInput from "../utility/mfa-input"
import { ThemedButton } from "../utility/input/buttons"
import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { MAX_CONTENT_WIDTH } from "../config"
import ThemedDialog from "../style/dialog"
import CircularCountdownTimer, { CountdownTimerRef } from "../utility/circular-countdown-timer"
import { log } from "../utility"
import Analytics from "../utility/analytics"
import { StackActions, useNavigation } from "@react-navigation/native"

const INITIAL_TIMER_DURATION = 30_000
const MAX_RESENDS_BEFORE_MODAL = 3

type Props = {
  route: {
    params: {
      token?: string
      state?: string
      shorthand?: string
      theme?: string
    }
  }
}

const MFA = ({ route }: Props): React.ReactElement<Props> => {
  const { theme, setTheme } = useContext(ThemeContext)
  const { textMain, success, bgMain } = theme.colors
  
  const inputRef = useRef<TextInput>(null)

  const [text, setText] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>(undefined)
  const [numResends, setNumResends] = useState<number>(0)
  const [showNoCodeModal, setShowNoCodeModal] = useState<boolean>(false)
  const [resendLocked, setResendLocked] = useState<boolean>(true)
  const timerRef = useRef<CountdownTimerRef>(null)

  const navigation = useNavigation()

  const { top, bottom } = useSafeAreaInsets()

  const BULLET_CHAR = "\u2022"
  const THREE_BULLETS = BULLET_CHAR.repeat(3)

  const shorthandDisplay = `${THREE_BULLETS} ${THREE_BULLETS} ${route.params?.shorthand || '0000'}`

  const handleTestCode = async () => {
    setLoading(true)
    try {
      api.setToken(route.params?.token)

      const { redirect_uri } = await api.checkMFA(text, route.params.state)

      Analytics.track('mfa_verified', { success: true })

      window.location.href = `${redirect_uri}?state=${route.params.state}&token=${route.params.token}&status=success`
    } catch (e) {
      Analytics.track('mfa_verified', { success: false })
      setError(e.message || "An unknown error occurred")
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    setLoading(true)
    setResendLocked(true)
    try {
      api.setToken(route.params?.token)
      await api.resendMFA()

      const nextNumResends = numResends + 1
      Analytics.track('mfa_resend_code', { num_resends: nextNumResends })

      setNumResends(nextNumResends)
      timerRef.current?.resetTimer(INITIAL_TIMER_DURATION, true)
    } catch (e) {
      setError(e.message || "An error occurred while resending the code")
      setResendLocked(false)
    } finally {
      setLoading(false)
    }
  }

  // handle params
  useEffect(() => {
    const params = route.params
    // if theme is provided, set it
    if (params?.theme) {
      setTheme(params.theme)
    }

    // if no token or shorthand defined, redirect to login
    if (!params?.token || !params?.shorthand) {
      if (!params?.token) {
        navigation.dispatch(StackActions.replace("Login"))
      }
    }
  }, [navigation, route.params, setTheme])

  // Log error when user hits max resends
  useEffect(() => {
    if (showNoCodeModal) {
      log.error(new Error('User did not receive code after max resends'), {
        tags: { message: 'user did not receive code after max resends' }
      })
    }
  }, [showNoCodeModal])

  // AUTO-SCROLL INPUT INTO VIEW ON MOBILE WEB
  useEffect(() => {
    const handler = (e: any) => {
      const target = e.target
      if (!target?.scrollIntoView) return

      setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }, 250)
    }

    document.addEventListener("focusin", handler)
    return () => document.removeEventListener("focusin", handler)
  }, [])

  return (
    <>
      <TouchableWithoutFeedback
        accessible={false}
        onPress={() => {
          inputRef.current?.blur()
        }}
      >
        <View
        style={{
          flex: 1,
          backgroundColor: bgMain,
          justifyContent: "center",
          alignItems: "center",
          paddingTop: top || 16,
          paddingBottom: bottom || 16,
          paddingHorizontal: 16,
        }}
      >
        <View style={{ width: "100%", maxWidth: MAX_CONTENT_WIDTH }}>
          <Text style={[textStyles.h3, {color: textMain, textAlign: "center"}]}>
            Enter your verification code
          </Text>
          <View style={{width: "100%", alignItems: "center", marginTop: 12}}>
            <MaterialCommunityIcons name="shield-key" color={textMain} size={150}/>
          </View>
          <Text style={[textStyles.body, {color: textMain, textAlign: "center", marginTop: 12}]}>
            We sent a verification code to {shorthandDisplay}
          </Text>
          <MFAInput
            inputRef={inputRef}
            value={text}
            onChangeText={setText}
            onSubmitEditing={handleTestCode}
            error={error}
          />
          <View style={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            marginTop: 8
          }}>
            <CircularCountdownTimer
              ref={timerRef}
              initialTimerDuration={INITIAL_TIMER_DURATION}
              startAutomatically
              width={20}
              height={20}
              strokeColor={textMain}
              strokeWidth={1}
              backgroundColor={'transparent'}
              foregroundColor={textMain}
              clockwise
              onTimerEnd={() => {
                setResendLocked(numResends >= MAX_RESENDS_BEFORE_MODAL)
                if (numResends >= MAX_RESENDS_BEFORE_MODAL) {
                  setShowNoCodeModal(true)
                }
              }}
            />
            <TouchableOpacity
              onPress={handleResendCode}
              disabled={resendLocked || loading}
            >
              <Text style={[
                textStyles.h4,
                { color: textMain, opacity: (resendLocked || loading) ? 0.5 : 1 }
              ]}>
                Resend code
              </Text>
            </TouchableOpacity>
          </View>
          <View style={{width: "100%", paddingVertical: 16}}>
            <ThemedButton
              text="Submit"
              onPress={handleTestCode}
              loading={loading}
              disabled={text?.length !== 6}
              variant="primary"
            />
          </View>
        </View>
      </View>
      </TouchableWithoutFeedback>

      {/* Error Modal */}
      <ThemedDialog
        visible={showNoCodeModal}
        title="Still no code?"
        subMessage={
          "We're sorry for the inconvenience. We care about your experience, " +
          "and our team has been notified to look into this right away."
        }
        errorAnimation={showNoCodeModal}
        options={[
          {
            title: 'Okay',
            onPress: () => {
              setShowNoCodeModal(false)
              setResendLocked(true)
            },
            role: 'confirm',
            icon: <Ionicons name="checkmark" size={22} />
          }
        ]}
      />
    </>
  )
}

export default MFA
